import { useState, FormEvent } from 'react';
import { ImageUploader } from '../ui/ImageUploader';
import { convertToAVIF, getPreferredExtension } from '../../lib/imageUtils';
import { apiUrl } from '../../lib/api';

interface MedicalFormProps {
	selectedDate: string;
	onSuccess?: (message: string) => void;
}

type MedicalSubTab = 'visit' | 'prescription';

const medicalSubTabs: { id: MedicalSubTab; label: string }[] = [
	{ id: 'visit', label: 'Kunjungan Dokter' },
	{ id: 'prescription', label: 'Resep' },
];

/**
 * MedicalForm Component
 *
 * Form for logging medical visits and prescriptions.
 * Posts to /api/medical/visits and /api/medical/prescriptions endpoints.
 */
export function MedicalForm({ selectedDate, onSuccess }: MedicalFormProps) {
	const [activeSubTab, setActiveSubTab] = useState<MedicalSubTab>('visit');

	// Visit form state
	const [doctorName, setDoctorName] = useState('');
	const [specialty, setSpecialty] = useState('');
	const [hospitalClinic, setHospitalClinic] = useState('');
	const [diagnosis, setDiagnosis] = useState('');
	const [visitNotes, setVisitNotes] = useState('');
	const [visitPendingBlobs, setVisitPendingBlobs] = useState<Blob[]>([]);
	const [visitUploaderKey, setVisitUploaderKey] = useState(0);

	// Prescription form state
	const [medicationName, setMedicationName] = useState('');
	const [dosage, setDosage] = useState('');
	const [frequency, setFrequency] = useState('');
	const [duration, setDuration] = useState('');
	const [prescriptionNotes, setPrescriptionNotes] = useState('');
	const [prescriptionPendingBlobs, setPrescriptionPendingBlobs] = useState<Blob[]>([]);
	const [prescriptionUploaderKey, setPrescriptionUploaderKey] = useState(0);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const resetVisitForm = () => {
		setDoctorName('');
		setSpecialty('');
		setHospitalClinic('');
		setDiagnosis('');
		setVisitNotes('');
		setVisitPendingBlobs([]);
		setVisitUploaderKey((k) => k + 1);
	};

	const resetPrescriptionForm = () => {
		setMedicationName('');
		setDosage('');
		setFrequency('');
		setDuration('');
		setPrescriptionNotes('');
		setPrescriptionPendingBlobs([]);
		setPrescriptionUploaderKey((k) => k + 1);
	};

	const uploadImages = async (entryType: string, entryId: string, blobs: Blob[]) => {
		const ext = await getPreferredExtension();
		for (const blob of blobs) {
			const result = await convertToAVIF(blob);

			const formData = new FormData();
			formData.append('file', result.blob, `image-${Date.now()}.${ext}`);
			formData.append('entryType', entryType);
			formData.append('entryId', entryId);
			formData.append('width', result.width.toString());
			formData.append('height', result.height.toString());

			const res = await fetch(apiUrl('/api/images'), {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			if (!res.ok) {
				throw new Error('Gagal mengunggah foto. Silakan coba lagi.');
			}
		}
	};

	const handleVisitSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		// Validate doctor name
		if (!doctorName.trim()) {
			setError('Nama dokter harus diisi');
			setIsSubmitting(false);
			return;
		}

		try {
			const res = await fetch(apiUrl('/api/medical/visits'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					date: selectedDate,
					doctorName: doctorName.trim(),
					specialty: specialty || undefined,
					hospitalClinic: hospitalClinic || undefined,
					diagnosis: diagnosis || undefined,
					notes: visitNotes || undefined,
				}),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan data');
			}

			const data = await res.json();

			// Upload images if any
			if (visitPendingBlobs.length > 0) {
				await uploadImages('visit', data.id, visitPendingBlobs);
			}

			onSuccess?.('Kunjungan dokter berhasil dicatat!');
			resetVisitForm();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handlePrescriptionSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		setError(null);

		// Validate medication name
		if (!medicationName.trim()) {
			setError('Nama obat harus diisi');
			setIsSubmitting(false);
			return;
		}

		try {
			const res = await fetch(apiUrl('/api/medical/prescriptions'), {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					date: selectedDate,
					medicationName: medicationName.trim(),
					dosage: dosage || undefined,
					frequency: frequency || undefined,
					duration: duration || undefined,
					notes: prescriptionNotes || undefined,
				}),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || 'Gagal menyimpan data');
			}

			const data = await res.json();

			// Upload images if any
			if (prescriptionPendingBlobs.length > 0) {
				await uploadImages('prescription', data.id, prescriptionPendingBlobs);
			}

			onSuccess?.('Resep berhasil dicatat!');
			resetPrescriptionForm();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Terjadi kesalahan');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="space-y-5">
			<h2 className="text-lg font-semibold text-gray-900">Catat Data Medis</h2>

			{/* Sub-tabs for form type */}
			<div className="flex gap-2 border-b border-gray-200 pb-2">
				{medicalSubTabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						onClick={() => {
							setActiveSubTab(tab.id);
							setError(null);
						}}
						className={`px-4 py-2.5 min-h-11 text-sm font-medium rounded-full transition ${
							activeSubTab === tab.id
								? 'bg-blue-100 text-blue-700'
								: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
						}`}
					>
						{tab.label}
					</button>
				))}
			</div>

			{/* Error Display */}
			{error && (
				<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
					{error}
				</div>
			)}

			{/* Visit Form */}
			{activeSubTab === 'visit' && (
				<form onSubmit={handleVisitSubmit} className="space-y-5">
					<div>
						<label
							htmlFor="doctor-name"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Nama Dokter <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							id="doctor-name"
							value={doctorName}
							onChange={(e) => setDoctorName(e.target.value)}
							placeholder="Contoh: Dr. Andi"
							required
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="specialty"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Spesialisasi (opsional)
						</label>
						<input
							type="text"
							id="specialty"
							value={specialty}
							onChange={(e) => setSpecialty(e.target.value)}
							placeholder="Contoh: Dokter Umum, Sp. Penyakit Dalam"
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="hospital-clinic"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Rumah Sakit/Klinik (opsional)
						</label>
						<input
							type="text"
							id="hospital-clinic"
							value={hospitalClinic}
							onChange={(e) => setHospitalClinic(e.target.value)}
							placeholder="Contoh: RS Siloam, Klinik Sehat"
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="diagnosis"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Diagnosis (opsional)
						</label>
						<textarea
							id="diagnosis"
							value={diagnosis}
							onChange={(e) => setDiagnosis(e.target.value)}
							rows={2}
							placeholder="Contoh: Diabetes mellitus tipe 2"
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="visit-notes"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Catatan (opsional)
						</label>
						<textarea
							id="visit-notes"
							value={visitNotes}
							onChange={(e) => setVisitNotes(e.target.value)}
							rows={3}
							placeholder="Catatan tambahan dari kunjungan..."
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					{/* Image Upload Section (inline) */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Foto (opsional)
						</label>
						<ImageUploader
							key={visitUploaderKey}
							entryType="visit"
							onPendingBlobsChange={setVisitPendingBlobs}
							disabled={isSubmitting}
						/>
					</div>

					{/* Submit Button - Sticky on mobile for easy access */}
					<div className="sticky bottom-0 pt-4 -mx-6 px-6 pb-1 bg-white sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:bg-transparent">
						<button
							type="submit"
							disabled={isSubmitting}
							className="w-full bg-blue-600 text-white py-3 px-4 min-h-12 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg sm:shadow-none"
						>
							{isSubmitting ? 'Menyimpan...' : 'Simpan Kunjungan'}
						</button>
					</div>
				</form>
			)}

			{/* Prescription Form */}
			{activeSubTab === 'prescription' && (
				<form onSubmit={handlePrescriptionSubmit} className="space-y-5">
					<p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
						Catat resep obat tanpa perlu dikaitkan dengan kunjungan dokter.
					</p>

					<div>
						<label
							htmlFor="medication-name"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Nama Obat <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							id="medication-name"
							value={medicationName}
							onChange={(e) => setMedicationName(e.target.value)}
							placeholder="Contoh: Metformin"
							required
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="dosage"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Dosis (opsional)
						</label>
						<input
							type="text"
							id="dosage"
							value={dosage}
							onChange={(e) => setDosage(e.target.value)}
							placeholder="Contoh: 500mg"
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="frequency"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Frekuensi (opsional)
						</label>
						<input
							type="text"
							id="frequency"
							value={frequency}
							onChange={(e) => setFrequency(e.target.value)}
							placeholder="Contoh: 2x sehari setelah makan"
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="duration"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Durasi (opsional)
						</label>
						<input
							type="text"
							id="duration"
							value={duration}
							onChange={(e) => setDuration(e.target.value)}
							placeholder="Contoh: 7 hari, 1 bulan"
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					<div>
						<label
							htmlFor="prescription-notes"
							className="block text-sm font-medium text-gray-700 mb-2"
						>
							Catatan (opsional)
						</label>
						<textarea
							id="prescription-notes"
							value={prescriptionNotes}
							onChange={(e) => setPrescriptionNotes(e.target.value)}
							rows={3}
							placeholder="Catatan tambahan..."
							disabled={isSubmitting}
							className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
						/>
					</div>

					{/* Image Upload Section (inline) */}
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Foto (opsional)
						</label>
						<ImageUploader
							key={prescriptionUploaderKey}
							entryType="prescription"
							onPendingBlobsChange={setPrescriptionPendingBlobs}
							disabled={isSubmitting}
						/>
					</div>

					{/* Submit Button - Sticky on mobile for easy access */}
					<div className="sticky bottom-0 pt-4 -mx-6 px-6 pb-1 bg-white sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:bg-transparent">
						<button
							type="submit"
							disabled={isSubmitting}
							className="w-full bg-blue-600 text-white py-3 px-4 min-h-12 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-lg sm:shadow-none"
						>
							{isSubmitting ? 'Menyimpan...' : 'Simpan Resep'}
						</button>
					</div>
				</form>
			)}
		</div>
	);
}
